import {
  AssistanceRequested,
  AssistanceResolved,
  CommandResult,
  CommandStatusChanged,
  EventChanged,
  ExtensionRequested,
  ExtensionResolved,
  PendingRequest,
  SessionState,
  SimulatorPresence,
  SimulatorStatus,
} from './events';
import {
  AssistanceType,
  CommandDirection,
  CommandStatus,
  EventChangeType,
  SignalType,
} from './enums';
let casingWarned = false;
export function warnOnLegacyCasing(payload: unknown): void {
  if (casingWarned || !payload || typeof payload !== 'object') {
    return;
  }
  const legacy = Object.keys(payload as Record<string, unknown>).some((key) =>
    key.includes('_'),
  );
  if (!legacy) {
    return;
  }
  casingWarned = true;
  console.warn(
    '[bay-manager-realtime] The stream is sending snake_case fields but this package (1.0.0) ' +
      'expects camelCase. The API and this package must be upgraded together. ' +
      'See CHANGELOG.md 1.0.0.',
  );
}
function toEnum<TEnum extends Record<string, string | number>>(
  enumObject: TEnum,
  value: string | number,
): number {
  if (typeof value === 'number') {
    return value;
  }
  const mapped = enumObject[value];
  return typeof mapped === 'number' ? mapped : Number(value);
}
interface WireCommandStatus {
  commandId: number;
  simulatorIdentifier: string;
  companyId: number;
  type: number | string;
  direction: number | string;
  status: number | string;
  detail: string | null;
  timestamp: string;
}
interface WireSimulatorStatus {
  simulatorIdentifier: string;
  companyId: number;
  isOnline: boolean;
  timestamp: string;
}
interface WireEventChange {
  eventId: number;
  simulatorIdentifier: string;
  companyId: number;
  changeType: number | string;
  startDateTime: string;
  endDateTime: string;
  closeActiveGame: boolean;
}
interface WireAssistanceRequest {
  simulatorIdentifier: string;
  companyId: number;
  type: number | string;
  requestedBy: string;
}
interface WireExtensionRequest {
  eventId: number;
  simulatorIdentifier: string;
  companyId: number;
  minutes: number;
  eventName: string;
}
interface WireExtensionResolved {
  eventId: number;
  simulatorIdentifier: string;
  companyId: number;
  approved: boolean;
  minutes: number;
  endDateTime: string;
}
interface WireSessionState {
  simulatorIdentifier: string;
  companyId: number;
  eventId: number;
  timestamp: string;
}
export function mapCommandStatus(
  wire: WireCommandStatus,
): CommandStatusChanged {
  return {
    commandId: wire.commandId,
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
    type: toEnum(SignalType, wire.type),
    direction: toEnum(CommandDirection, wire.direction),
    status: toEnum(CommandStatus, wire.status),
    detail: wire.detail ?? null,
    timestamp: wire.timestamp,
  };
}
export function mapSimulatorPresence(
  wire: WireSimulatorStatus,
): SimulatorPresence {
  return {
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
    isOnline: wire.isOnline,
    timestamp: wire.timestamp,
  };
}
export function mapEventChanged(wire: WireEventChange): EventChanged {
  return {
    eventId: wire.eventId,
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
    changeType: toEnum(EventChangeType, wire.changeType),
    startDateTime: wire.startDateTime,
    endDateTime: wire.endDateTime,
    closeActiveGame: wire.closeActiveGame,
  };
}
export function mapAssistanceRequested(
  wire: WireAssistanceRequest,
): AssistanceRequested {
  return {
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
    type: toEnum(AssistanceType, wire.type),
    requestedBy: wire.requestedBy,
  };
}
export function mapExtensionRequested(
  wire: WireExtensionRequest,
): ExtensionRequested {
  return {
    eventId: wire.eventId,
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
    minutes: wire.minutes,
    eventName: wire.eventName,
  };
}
export function mapExtensionResolved(
  wire: WireExtensionResolved,
): ExtensionResolved {
  return {
    eventId: wire.eventId,
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
    approved: wire.approved,
    minutes: wire.minutes,
    endDateTime: wire.endDateTime,
  };
}
export function mapSessionState(wire: WireSessionState): SessionState {
  return {
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
    eventId: wire.eventId,
    timestamp: wire.timestamp,
  };
}
interface WireCommandResult {
  id: number;
  simulatorIdentifier: string;
  companyId: number;
  type: number | string;
  direction: number | string;
  status: number | string;
  parameters: string | null;
  detail: string | null;
  expiresAt: string | null;
}
interface WireSimulatorStatus {
  simulatorIdentifier: string;
  isOnline: boolean;
}
export function mapCommandResult(wire: WireCommandResult): CommandResult {
  return {
    id: wire.id,
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
    type: toEnum(SignalType, wire.type),
    direction: toEnum(CommandDirection, wire.direction),
    status: toEnum(CommandStatus, wire.status),
    parameters: wire.parameters ?? null,
    detail: wire.detail ?? null,
    expiresAt: wire.expiresAt ?? null,
  };
}
export function mapSimulatorStatus(wire: WireSimulatorStatus): SimulatorStatus {
  return {
    simulatorIdentifier: wire.simulatorIdentifier,
    isOnline: wire.isOnline,
  };
}
interface WirePendingRequest {
  kind: string;
  simulatorIdentifier: string;
  companyId: number;
  eventId: number | null;
  minutes: number | null;
  eventName: string | null;
  requestedBy: string | null;
  requestedAt: string;
  expiresAt: string;
}
export function mapPendingRequest(wire: WirePendingRequest): PendingRequest {
  return {
    kind: wire.kind === 'extension' ? 'extension' : 'assistance',
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
    eventId: wire.eventId ?? null,
    minutes: wire.minutes ?? null,
    eventName: wire.eventName ?? null,
    requestedBy: wire.requestedBy ?? null,
    requestedAt: wire.requestedAt,
    expiresAt: wire.expiresAt,
  };
}
interface WireAssistanceResolved {
  simulatorIdentifier: string;
  companyId: number;
}
export function mapAssistanceResolved(
  wire: WireAssistanceResolved,
): AssistanceResolved {
  return {
    simulatorIdentifier: wire.simulatorIdentifier,
    companyId: wire.companyId,
  };
}
